import React, { useState, useRef, useEffect } from 'react';
import { Mail, Phone, FileCheck, Calendar, MoreVertical } from 'lucide-react';
import { Candidate } from '../../api/candidates';
import { ReferenceWithAnswers } from '../../api/references';
import StatusBadge, { StatusIndicator } from './StatusBadge';
import { useLanguage } from '../../contexts/LanguageContext';
import SendEmailButton from './SendEmailButton';
import CopyLinkButton from './CopyLinkButton';
import EvaluateWithAIButton from './EvaluateWithAIButton';
import AddReferenceButton from './AddReferenceButton';
import { useClickOutside } from '../../hooks/useClickOutside';

interface CandidatesGridProps {
  selectedJobId: string;
  candidates: Candidate[];
  onCandidateClick: (candidate: Candidate) => void;
  isJobCompleted?: boolean;
}

const CandidatesGrid = ({ candidates, onCandidateClick, isJobCompleted = false }: CandidatesGridProps) => {
  const { t } = useLanguage();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  
  const getCandidateReferences = (candidateId: string): ReferenceWithAnswers[] => {
    return candidates.find(c => c.id === candidateId)?.references || [];
  };
  
  const toggleMenu = (e: React.MouseEvent, candidateId: string) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === candidateId ? null : candidateId);
  };
  
  const closeMenu = () => {
    setOpenMenuId(null);
  };
  
  // Close menu when clicking outside or on escape key
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const menus = document.querySelectorAll('.candidate-menu');
      let clickedInside = false;
      
      menus.forEach(menu => {
        if (menu.contains(e.target as Node)) {
          clickedInside = true;
        }
      });
      
      if (!clickedInside && openMenuId) {
        closeMenu();
      }
    };
    
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openMenuId) {
        closeMenu();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [openMenuId]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
      {candidates.map((candidate) => (
        <div
          key={candidate.id}
          className="bg-white rounded-xl border hover:shadow-lg transition-all duration-200 cursor-pointer group"
          onClick={() => onCandidateClick(candidate)}
        >
          <div className="p-6 relative">
            {/* Three-dot menu button */}
            <div className="absolute top-4 right-4 candidate-menu">
              <button 
                onClick={(e) => toggleMenu(e, candidate.id)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <MoreVertical className="w-5 h-5 text-gray-500" />
              </button>
              
              {/* Dropdown menu */}
              {openMenuId === candidate.id && (
                <div 
                  className="absolute right-0 top-8 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10 overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="py-1">
                    {!isJobCompleted && (
                      <div className="px-2 py-1.5">
                        <SendEmailButton 
                          candidateId={candidate.id} 
                          email={candidate.email} 
                          disabled={isJobCompleted} 
                          className="w-full justify-center"
                        />
                      </div>
                    )}
                    
                    <div className="px-2 py-1.5">
                      <EvaluateWithAIButton 
                        candidateId={candidate.id} 
                        candidateName={candidate.name}
                      />
                    </div>
                    
                    {!isJobCompleted && (
                      <div className="px-2 py-1.5">
                        <AddReferenceButton 
                          candidateId={candidate.id}
                          onReferenceAdded={() => window.location.reload()}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Candidate Info */}
            <div className="flex items-start gap-4">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                  {candidate.name}
                </h3>
                <div className="mt-1 space-y-1">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Mail className="w-4 h-4" />
                    <span className="truncate">{candidate.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Phone className="w-4 h-4" />
                    <span>{candidate.phone}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <div className="flex items-center justify-between text-sm text-gray-500 mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <FileCheck className="w-4 h-4" />
                    <span>{t('candidates.candidateCard.references')}</span>
                  </div>
                  <span className="font-medium">
                    {candidate._referenceCount || `${candidate.referencesCompleted} / ${candidate.referencesRequired}`}
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all duration-300"
                    style={{
                      width: `${!candidate.form_filled_at || !candidate.referencesRequired 
                        ? 0 
                        : Math.min(100, (candidate.referencesCompleted / (candidate.referencesRequired || 1)) * 100)}%`
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <StatusIndicator 
                    formFilled={!!candidate.form_filled_at}
                    referencesCompleted={candidate.referencesCompleted}
                    referencesTotal={candidate.referencesRequired}
                  />
                </div>
                <StatusBadge 
                  formFilled={!!candidate.form_filled_at}
                  referencesCompleted={candidate.referencesCompleted}
                  referencesTotal={candidate.referencesRequired}
                />
              </div>

              <div className="pt-4 border-t flex items-center justify-between text-sm">
                <div className="flex items-center gap-1.5 text-gray-500">
                  <Calendar className="w-4 h-4" />
                  <span>{t('candidates.candidateCard.formSubmitted')}</span>
                </div>
                <span className="font-medium text-gray-900">
                  {candidate.form_filled_at ? new Date(candidate.form_filled_at).toLocaleDateString() : t('candidates.candidateCard.notSubmitted')}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CandidatesGrid;